import React from "react";
import {Link} from "react-router-dom";
import {HashLink} from "react-router-hash-link";

/**
 * EAS landing — hero. Big display headline, lede, two CTAs (primary → /system),
 * plus a decorative "operating system" preview card to the right.
 * Headline + lede stagger via framer-motion in MainContent; here we keep static
 * markup so this component stays portable.
 */
export default function EasHero(): JSX.Element {
    return (
        <header style={heroStyle}>
            <div style={heroBgGlow}/>
            <div className="eas-container eas-grid-hero">
                <div>
                    <span style={eyebrowStyle}>
                        <span style={pulseDotStyle}/>
                        For parents · UK &amp; US
                    </span>
                    <h1 className="eas-h1-hero" style={h1Style}>
                        Your teen, <em style={emStyle}>running their own life</em>. Calmly. Ambitiously.
                    </h1>
                    <p style={ledeStyle}>
                        We build operating systems for teenagers — practical programs that
                        replace nagging with self-direction. <strong style={{color: "#fff", fontWeight: 500}}>Effective Action System</strong>
                        {" "}is the six-week course that started it all.
                    </p>
                    <div style={{display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center"}}>
                        <Link to="/system" className="eas-btn eas-btn--primary">
                            See the system <span className="eas-arr">→</span>
                        </Link>
                        <HashLink smooth to="/#mission" className="eas-btn eas-btn--ghost">
                            Why we built it
                        </HashLink>
                    </div>
                </div>

                <HeroVisual/>
            </div>
        </header>
    );
}

function HeroVisual(): JSX.Element {
    return (
        <div style={visualStyle}>
            <div style={visualGlow}/>
            <div style={{display: "flex", flexDirection: "column", gap: 12, height: "100%", position: "relative"}}>
                <Card>
                    <Label>Today · 14 May</Label>
                    <Task done time="9:00">Math — sample exam, 90 min</Task>
                    <Task done time="11:00">English — read 1 chapter</Task>
                    <Task time="17:00">Football practice</Task>
                </Card>
                <Card>
                    <Label>Weekly focus</Label>
                    <div style={{fontFamily: '"Fraunces", serif', fontSize: 18, color: "#fff", margin: "6px 0"}}>
                        Land an A on chemistry mid-term
                    </div>
                    <div style={{height: 4, background: "rgba(255,255,255,0.08)", borderRadius: 4, overflow: "hidden", marginTop: 6}}>
                        <div style={{height: "100%", width: "72%", background: "#5ce08d", borderRadius: 4}}/>
                    </div>
                    <div style={metaTextStyle}>72% complete · 3 days left</div>
                </Card>
                <Card>
                    <Label>Streak</Label>
                    <div style={{display: "flex", gap: 4}}>
                        {[1, 1, 1, 1, 1, 1, 0].map((on, i) => (
                            <span key={i} style={{width: 18, height: 18, borderRadius: 4, background: on ? "#5ce08d" : "rgba(255,255,255,0.08)"}}/>
                        ))}
                    </div>
                    <div style={metaTextStyle}>6 days · keep going</div>
                </Card>
            </div>
        </div>
    );
}

const Card = ({children}: {children: React.ReactNode}): JSX.Element => (
    <div style={{background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 14, padding: 16, backdropFilter: "blur(8px)", position: "relative"}}>
        {children}
    </div>
);
const Label = ({children}: {children: React.ReactNode}): JSX.Element => (
    <div style={{fontFamily: '"JetBrains Mono", monospace', fontSize: 10, letterSpacing: "0.14em", color: "rgba(255,255,255,0.4)", textTransform: "uppercase", marginBottom: 8}}>{children}</div>
);
const Task = ({children, done, time}: {children: React.ReactNode; done?: boolean; time: string}): JSX.Element => (
    <div style={{display: "flex", alignItems: "center", gap: 12, fontSize: 13, color: "rgba(255,255,255,0.85)", marginTop: 10}}>
        <div style={{
            width: 18, height: 18, borderRadius: "50%",
            border: "1.5px solid #5ce08d", flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: done ? "#5ce08d" : "transparent",
            color: "#0b1410", fontSize: 11, fontWeight: 700,
        }}>{done ? "✓" : ""}</div>
        <span>{children}</span>
        <span style={{fontFamily: '"JetBrains Mono", monospace', fontSize: 11, color: "#5ce08d", marginLeft: "auto"}}>{time}</span>
    </div>
);

const heroStyle: React.CSSProperties = {padding: "100px 0 140px", position: "relative", overflow: "hidden"};
const heroBgGlow: React.CSSProperties = {position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)", width: 1200, height: 1200, background: "radial-gradient(circle, rgba(42,209,103,0.15), transparent 60%)", pointerEvents: "none", filter: "blur(40px)"};
const eyebrowStyle: React.CSSProperties = {fontFamily: '"JetBrains Mono", monospace', fontSize: 12, letterSpacing: "0.18em", textTransform: "uppercase", color: "#5ce08d", margin: "0 0 24px", display: "inline-flex", alignItems: "center", gap: 10, padding: "8px 14px", background: "rgba(42,209,103,0.1)", border: "1px solid rgba(42,209,103,0.2)", borderRadius: 999};
const pulseDotStyle: React.CSSProperties = {width: 6, height: 6, borderRadius: "50%", background: "#5ce08d", animation: "easPulse 2s ease infinite"};
const h1Style: React.CSSProperties = {fontFamily: '"Fraunces", serif', fontWeight: 300, fontSize: "clamp(48px, 6.4vw, 92px)", lineHeight: 0.96, letterSpacing: "-0.03em", margin: "0 0 28px", color: "#fff", textWrap: "balance" as any};
const emStyle: React.CSSProperties = {fontStyle: "italic", color: "#5ce08d", fontWeight: 400};
const ledeStyle: React.CSSProperties = {fontSize: 19, lineHeight: 1.6, maxWidth: 540, color: "rgba(255,255,255,0.7)", margin: "0 0 36px"};
const visualStyle: React.CSSProperties = {position: "relative", aspectRatio: "1/1.1", background: "linear-gradient(160deg, rgba(255,255,255,0.04), rgba(42,209,103,0.04))", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 32, padding: 28, overflow: "hidden"};
const visualGlow: React.CSSProperties = {position: "absolute", inset: 0, background: "radial-gradient(ellipse at top right, rgba(42,209,103,0.18), transparent 50%)", pointerEvents: "none"};
const metaTextStyle: React.CSSProperties = {fontFamily: '"JetBrains Mono", monospace', fontSize: 11, color: "rgba(255,255,255,0.5)", marginTop: 8, letterSpacing: "0.1em"};
